import React, { useEffect, useMemo, useState } from "react";
import debounce from "lodash/debounce";
import { connect } from "react-redux";
import { useSearchParams } from "react-router-dom";
import {
  addStockOpname,
  deleteStockOpname,
  fetchStockOpnames,
  processStockOpname,
  updateStockOpname,
} from "../../actions/stockOpnameAction";
import { fetchMaterials } from "../../actions/materialAction";
import { fetchProducts } from "../../actions/productAction";
import { AlertConfirmModal } from "../../components/Modals/AlertConfirmModal";
import AkasSelect from "../../components/AkasSelect";
import { sortHelper } from "../../utils/helpers/sortHelper";
import DetailStockOpname from "./DetailStockOpname";
import FormStockOpname from "./FormStockOpname";
import { hasAnyPermission } from "../../utils/helpers/permissions";

const getToday = () => {
  const date = new Date();
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const createInitialState = () => ({
  id: "",
  opname_no: "",
  opname_date: getToday(),
  status: "draft",
  note: "",
  details: [
    {
      item_type: "material",
      item_id: "",
      item_name: "",
      unit: "",
      system_stock: 0,
      physical_stock: 0,
      note: "",
    },
  ],
});

const formatDate = (date) => {
  if (!date) return "-";
  return new Intl.DateTimeFormat("id-ID", { day: "2-digit", month: "short", year: "numeric" }).format(
    new Date(`${date.slice(0, 10)}T00:00:00`),
  );
};

const StockOpnamePage = (props) => {
  const {
    dataStockOpnames,
    stockOpnames,
    materials,
    products,
    isLoading,
    error,
    fetchStockOpnames,
    addStockOpname,
    updateStockOpname,
    processStockOpname,
    deleteStockOpname,
    fetchMaterials,
    fetchProducts,
    userProfile,
  } = props;
  const canProcess = hasAnyPermission(userProfile, ["process-stock-opnames"]);
  const canCreate = hasAnyPermission(userProfile, ["create-stock-opnames"]);
  const canReadMaterials = hasAnyPermission(userProfile, ["view-materials"]);
  const canReadProducts = hasAnyPermission(userProfile, ["view-products", "view-product-reference"]);

  const [perPage, setPerPage] = useState(100);
  const [currentPage, setCurrentPage] = useState(1);
  const [isEdit, setIsEdit] = useState(false);
  const [statusFilter, setStatusFilter] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();
  const searchFromUrl = searchParams.get("search") || "";
  const [filterText, setFilterText] = useState(searchFromUrl);
  const [formState, setFormState] = useState(createInitialState());
  const { sortConfig, handleSort, getSortedData } = sortHelper.useSort();

  const handlePerPageChange = (value) => {
    setPerPage(value);
    setCurrentPage(1);
  };

  const openModal = () => {
    setFormState(createInitialState());
    setIsEdit(false);
  };

  const closeModal = () => {
    setFormState(createInitialState());
    setIsEdit(false);
  };

  const refreshData = () => fetchStockOpnames(perPage, currentPage, searchFromUrl, statusFilter);

  const toPayload = () => ({
    opname_date: formState.opname_date,
    note: formState.note || null,
    details: formState.details.map((detail) => ({
      item_type: detail.item_type,
      item_id: Number(detail.item_id),
      physical_stock: Number(detail.physical_stock),
      note: detail.note || null,
    })),
  });

  const handleAdd = async () => {
    const success = await addStockOpname(toPayload());
    if (success) {
      closeModal();
      refreshData();
    }
    return success;
  };

  const handleUpdate = async () => {
    const success = await updateStockOpname(formState.id, toPayload());
    if (success) {
      closeModal();
      refreshData();
    }
    return success;
  };

  const handleProcess = async (id) => {
    if (!canProcess) return false;
    const success = await processStockOpname(id);
    if (success) {
      closeModal();
      refreshData();
    }
  };

  const handleDelete = async (id) => {
    const success = await deleteStockOpname(id);
    if (success) {
      closeModal();
      refreshData();
    }
  };

  const handleSearchChange = (event) => {
    const value = event.target.value;
    setFilterText(value);
    if (value) {
      setSearchParams({ search: value });
    } else {
      setSearchParams({});
    }
  };

  useEffect(() => {
    setFilterText(searchFromUrl);
  }, [searchFromUrl]);

  const debouncedFetch = useMemo(
    () =>
      debounce((page, limit, search, status) => {
        fetchStockOpnames(limit, page, search, status);
      }, 500),
    [fetchStockOpnames],
  );

  useEffect(() => {
    debouncedFetch(currentPage, perPage, searchFromUrl, statusFilter);
    return () => debouncedFetch.cancel();
  }, [currentPage, perPage, searchFromUrl, statusFilter, debouncedFetch]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchFromUrl, statusFilter]);

  useEffect(() => {
    if (canReadMaterials) fetchMaterials(100);
    if (canReadProducts) fetchProducts(100);
  }, [fetchMaterials, fetchProducts, canReadMaterials, canReadProducts]);

  return (
    <div className="page-wrapper">
      <div className="page-header d-print-none">
        <div className="container-xl">
          <div className="row g-2 align-items-center">
            <div className="col">
              <h1 className="page-title fs-1 text-capitalize">Data Stock Opname</h1>
              <div className="page-pretitle">kontrol dan penyesuaian stok</div>
            </div>
            <div className="col-auto ms-auto d-print-none">
              {canCreate ? <div className="btn-list">
                <button
                  type="button"
                  className="btn btn-primary btn-5 d-none d-sm-inline-block"
                  data-bs-toggle="modal"
                  data-bs-target="#modal-form-stock-opname"
                  onClick={openModal}>
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    className="icon icon-2">
                    <path d="M12 5v14M5 12h14" />
                  </svg>
                  Tambah stock opname
                </button>
                <button
                  type="button"
                  className="btn btn-primary btn-6 d-sm-none btn-icon"
                  data-bs-toggle="modal"
                  data-bs-target="#modal-form-stock-opname"
                  onClick={openModal}>
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    className="icon icon-2">
                    <path d="M12 5v14M5 12h14" />
                  </svg>
                </button>
              </div> : null}
            </div>
          </div>
        </div>
      </div>

      <div className="page-body">
        <div className="container-xl">
          <div className="row row-deck row-cards">
            <div className="col-12">
              <div className="card">
                <div className="card-table">
                  <div className="card-header">
                    <div className="row w-full align-items-center">
                      <div className="col-lg-6 col-12">
                        <h3 className="card-title mb-0">Stock Opname</h3>
                        <p className="text-secondary m-0">Pencatatan hasil pemeriksaan stok fisik</p>
                      </div>
                      <div className="col-lg-3 col-md-5 col-12 my-lg-0 my-2">
                        <AkasSelect
                          className="form-select"
                          value={statusFilter}
                          onChange={(event) => setStatusFilter(event.target.value)}>
                          <option value="">Semua Status</option>
                          <option value="draft">Draft</option>
                          <option value="completed">Completed</option>
                        </AkasSelect>
                      </div>
                      <div className="col-lg-3 col-md-7 col-12 my-lg-0 my-2">
                        <div className="input-group input-group-flat w-auto">
                          {isLoading ? (
                            <span className="input-group-text">
                              <div
                                className="spinner-border spinner-border-sm text-primary text-center"
                                role="status"></div>
                            </span>
                          ) : (
                            <span className="input-group-text">
                              <svg
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                className="icon icon-1">
                                <circle cx="10" cy="10" r="7" />
                                <path d="M21 21l-6-6" />
                              </svg>
                            </span>
                          )}
                          <input
                            type="text"
                            className="form-control"
                            autoComplete="off"
                            onChange={handleSearchChange}
                            placeholder="Cari..."
                            value={filterText}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {error ? <div className="alert alert-danger m-3 mb-0">{error}</div> : null}

                  <div id="advanced-table">
                    <div className="table-responsive">
                      <table className="table table-vcenter table-selectable">
                        <thead className="table-header">
                          <tr>
                            <th className="sort-name py-3 w-1">No.</th>
                            <sortHelper.SortableHeader
                              label="Nomor"
                              sortKey="opname_no"
                              sortConfig={sortConfig}
                              onSort={handleSort}
                            />
                            <sortHelper.SortableHeader
                              label="Tanggal"
                              sortKey="opname_date"
                              sortConfig={sortConfig}
                              onSort={handleSort}
                            />
                            <th className="fw-bold">Status</th>
                            <sortHelper.SortableHeader
                              label="Total Item"
                              sortKey="total_items"
                              sortConfig={sortConfig}
                              onSort={handleSort}
                            />
                            <sortHelper.SortableHeader
                              label="Total Selisih"
                              sortKey="total_difference"
                              sortConfig={sortConfig}
                              onSort={handleSort}
                            />
                            <th className="fw-bold">Catatan</th>
                            <th className="text-start" style={{ width: "30%" }}></th>
                          </tr>
                        </thead>
                        <tbody className="table-tbody">
                          {isLoading ? (
                            [...Array(stockOpnames?.length || 5)].map((_, index) => (
                              <tr key={`loading-${index}`} style={{ cursor: "loader" }}>
                                <td className="sort-name py-4">{(currentPage - 1) * perPage + index + 1}.</td>
                                {[...Array(6)].map((__, placeholderIndex) => (
                                  <td key={placeholderIndex}>
                                    <div className="placeholder placeholder-lg w-75"></div>
                                  </td>
                                ))}
                                <td className="justify-content-start text-start flex-nowrap">
                                  <div className="placeholder placeholder-lg me-2" style={{ width: "30%" }}></div>
                                  <div className="placeholder placeholder-lg me-2" style={{ width: "30%" }}></div>
                                  <div className="placeholder placeholder-lg" style={{ width: "30%" }}></div>
                                </td>
                              </tr>
                            ))
                          ) : stockOpnames.length === 0 ? (
                            <tr>
                              <td colSpan="8" className="text-center text-secondary py-5">
                                Belum ada data stock opname.
                              </td>
                            </tr>
                          ) : (
                            getSortedData(stockOpnames).map((stockOpname, index) => (
                              <tr key={stockOpname.id}>
                                <td>{(currentPage - 1) * perPage + index + 1}.</td>
                                <td>
                                  <span className="badge fw-bold">{stockOpname.opname_no}</span>
                                </td>
                                <td>{formatDate(stockOpname.opname_date)}</td>
                                <td>
                                  <span
                                    className={`badge ${stockOpname.status === "completed" ? "bg-success-lt" : "bg-warning-lt"}`}>
                                    {stockOpname.status === "completed" ? "Completed" : "Draft"}
                                  </span>
                                </td>
                                <td>{stockOpname.total_items} item</td>
                                <td>
                                  <span
                                    className={`badge ${stockOpname.total_difference === 0 ? "bg-success-lt" : stockOpname.total_difference > 0 ? "bg-blue-lt" : "bg-danger-lt"}`}>
                                    {stockOpname.total_difference > 0 ? "+" : ""}
                                    {stockOpname.total_difference}
                                  </span>
                                </td>
                                <td className="text-secondary">{stockOpname.note || "-"}</td>
                                <td>
                                  <div className="btn-list flex-nowrap justify-content-center">
                                    <button
                                      type="button"
                                      className="btn btn-1 bg-primary-lt"
                                      data-bs-toggle="modal"
                                      data-bs-target="#modal-detail-stock-opname"
                                      onClick={() => setFormState({ ...stockOpname })}>
                                      <svg
                                        width="20"
                                        height="20"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="1.5"
                                        className="icon">
                                        <path d="M2 12s3.5-7 10-7s10 7 10 7s-3.5 7-10 7s-10-7-10-7" />
                                        <circle cx="12" cy="12" r="3" />
                                      </svg>
                                      Detail
                                    </button>
                                    {stockOpname.status === "draft" ? (
                                      <>
                                        {hasAnyPermission(userProfile, ["edit-stock-opnames"]) ? (<button
                                          type="button"
                                          className="btn btn-1 bg-secondary-lt"
                                          data-bs-toggle="modal"
                                          data-bs-target="#modal-form-stock-opname"
                                          onClick={() => {
                                            setIsEdit(true);
                                            setFormState({
                                              ...stockOpname,
                                              opname_date: stockOpname.opname_date?.slice(0, 10),
                                              details: stockOpname.details || [],
                                            });
                                          }}>
                                          <svg
                                            width="20"
                                            height="20"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="1.5"
                                            className="icon">
                                            <path d="M4 20h4l11-11a2.8 2.8 0 0 0-4-4l-11 11v4M13.5 6.5l4 4" />
                                          </svg>
                                          Edit
                                        </button>) : null}
                                        {canProcess ? (<button
                                          type="button"
                                          className="btn btn-1 bg-success-lt"
                                          data-bs-toggle="modal"
                                          data-bs-target="#modal-process-stock-opname"
                                          onClick={() => setFormState({ ...stockOpname })}>
                                          <svg
                                            width="20"
                                            height="20"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="1.5"
                                            className="icon">
                                            <path d="M5 12l5 5l9-9" />
                                          </svg>
                                          Proses
                                        </button>) : null}
                                        {hasAnyPermission(userProfile, ["delete-stock-opnames"]) ? (<button
                                          type="button"
                                          className="btn btn-1 bg-danger-lt"
                                          data-bs-toggle="modal"
                                          data-bs-target="#modal-delete-stock-opname"
                                          onClick={() => setFormState({ ...stockOpname })}>
                                          <svg
                                            width="20"
                                            height="20"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="1.5"
                                            className="icon">
                                            <path d="M4 7h16M10 11v6M14 11v6M5 7l1 14h12l1-14M9 7V4h6v3" />
                                          </svg>
                                          Hapus
                                        </button>) : null}
                                      </>
                                    ) : null}
                                  </div>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>

                    <div className="card-footer d-flex align-items-center">
                      <div className="dropdown">
                        <button className="btn dropdown-toggle" data-bs-toggle="dropdown">
                          <span className="me-1">{perPage}</span>
                          <span>records</span>
                        </button>
                        <div className="dropdown-menu">
                          {[10, 20, 50, 100].map((value) => (
                            <button key={value} className="dropdown-item" onClick={() => handlePerPageChange(value)}>
                              {value} records
                            </button>
                          ))}
                        </div>
                      </div>
                      <p className="ms-2 my-auto text-secondary">
                        {dataStockOpnames?.per_page || 0} dari <span>{dataStockOpnames?.total || 0}</span> data
                      </p>
                      <ul className="pagination m-0 ms-auto">
                        <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                          <button
                            className="page-link"
                            disabled={currentPage === 1}
                            onClick={() => setCurrentPage((previous) => Math.max(previous - 1, 1))}>
                            <svg width="24" height="24" stroke="currentColor" fill="none" viewBox="0 0 24 24">
                              <path d="M15 6l-6 6l6 6" />
                            </svg>
                          </button>
                        </li>
                        {dataStockOpnames?.links?.slice(1, -1)?.map((item, index) => {
                          const pageNumber = item.url ? new URL(item.url).searchParams.get("page") : null;
                          return (
                            <li key={index} className={`page-item ${item.active ? "active" : ""}`}>
                              <button
                                className="page-link"
                                disabled={!pageNumber}
                                onClick={() => pageNumber && setCurrentPage(Number(pageNumber))}>
                                {item.label}
                              </button>
                            </li>
                          );
                        })}
                        <li className={`page-item ${currentPage === dataStockOpnames?.last_page ? "disabled" : ""}`}>
                          <button
                            className="page-link"
                            disabled={!dataStockOpnames?.next_page_url}
                            onClick={() => setCurrentPage(Number(dataStockOpnames?.current_page) + 1)}>
                            <svg width="24" height="24" stroke="currentColor" fill="none" viewBox="0 0 24 24">
                              <path d="M9 6l6 6l-6 6" />
                            </svg>
                          </button>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <FormStockOpname
        isEdit={isEdit}
        closeModal={closeModal}
        formState={formState}
        setFormState={setFormState}
        materials={materials}
        products={products}
        isLoading={isLoading}
        handleAdd={handleAdd}
        handleUpdate={handleUpdate}
      />
      <DetailStockOpname stockOpname={formState} closeModal={closeModal} />
      <AlertConfirmModal
        type="warning"
        title="Proses stock opname?"
        message={`Stok material dan produk akan disesuaikan berdasarkan ${formState.opname_no || "data ini"}.`}
        confirmText="Proses"
        cancelText="Batal"
        idModal="process-stock-opname"
        idData={formState.id}
        onConfirm={handleProcess}
        onCancel={closeModal}
      />
      <AlertConfirmModal
        type="error"
        message={`Menghapus data ${formState.opname_no || "stock opname"}`}
        confirmText="Hapus"
        cancelText="Batal"
        idModal="delete-stock-opname"
        idData={formState.id}
        onConfirm={handleDelete}
        onCancel={closeModal}
      />
    </div>
  );
};

const mapStateToProps = (state) => ({
  userProfile: state?.auth?.login?.profile,
  dataStockOpnames: state?.stockOpnames?.datas,
  stockOpnames: state?.stockOpnames?.datas?.data || [],
  materials: (state?.materials?.datas?.data || []).filter((material) => !material.is_virtual_formula),
  products: (state?.products?.datas?.data || []).filter(
    (product) => !product?.inventory_mode || product.inventory_mode === "stocked",
  ),
  isLoading: state?.stockOpnames?.loading,
  error: state?.stockOpnames?.error,
});

export default connect(mapStateToProps, {
  fetchStockOpnames,
  addStockOpname,
  updateStockOpname,
  processStockOpname,
  deleteStockOpname,
  fetchMaterials,
  fetchProducts,
})(StockOpnamePage);
